/**
 * ENG-2990 — tell the extension its org policy changed, from anywhere in the console.
 *
 * ## The gap this closes
 *
 * The extension polls `/subscription-status` on a five-minute freshness window, so any policy
 * change reaches it eventually. `org-policy-updated` is what makes it immediate for the admin who
 * made the change — the one person about to check whether it worked.
 *
 * That message was wired straight into `useOrgSettings` (ENG-2671) for enforcement mode. The extension
 * allow-list and, since ENG-2985, the deny list are policy the Shield reads just as directly, and
 * neither fired it: an admin denying an extension watched their own browser keep exempting it for up
 * to five minutes, while the same admin flipping enforcement saw it instantly. The inconsistency is the
 * tell — nothing about a deny makes it less urgent than an enforcement flip.
 *
 * ENG-3416: feature flags, at the organization's tier and a group's, were the same gap. This comment
 * used to say `useOrgSettings` covered them, and it did not: turning the deny list on reached no
 * browser, the admin's included, before its five-minute refresh. ENG-3434: so was saving locked
 * policy or site overrides, at either tier, and those call it now too.
 *
 * ## Why a registered notifier rather than a callback per hook
 *
 * The transport needs the detected extension ids, which live in `useExtension`'s state, so only
 * `App.tsx` can build it (`useOrgPolicyNotifier`). Passing it down would mean threading a callback
 * through DashboardLayout into ExtensionsPage for the deny hook alone — and the next policy hook would
 * have to remember to ask for it. A free function any hook can call is the version where forgetting is
 * the unusual act rather than the default.
 *
 * Best-effort by construction, exactly like the path it mirrors: a browser with no extension, or one
 * that does not answer, must never fail a save. The poll is what guarantees delivery.
 */
type Notifier = () => void;

let notifier: Notifier | null = null;

/** Registered once by `App.tsx`, which is the only place that can reach the extension ids. */
export function setOrgPolicyNotifier(fn: Notifier | null): void {
  notifier = fn;
}

/**
 * Call after a policy mutation the extension reads. A no-op before registration or outside the
 * console (tests, the login shell) — never throws, so a caller needs no guard.
 */
export function notifyOrgPolicyChanged(): void {
  try {
    notifier?.();
  } catch {
    // The transport is already best-effort; a failure here must not surface as a failed save.
  }
}

/**
 * ENG-3527 — the console's own copies of what policy decides, for a hook App.tsx cannot reach.
 *
 * The notifier tells what App.tsx holds by name (the Risk Report, Overview, the console's plan). A hook
 * held further down — Overview's "Needs your attention" lives in DashboardLayout — listens here instead,
 * and the notifier announces to every listener after each policy write. Returns the unsubscribe.
 */
type Listener = () => void;

const listeners = new Set<Listener>();

export function onOrgPolicyChanged(fn: Listener): () => void {
  listeners.add(fn);
  return () => { listeners.delete(fn); };
}

/** Called by the notifier (`useOrgPolicyNotifier`). One listener throwing does not stop the rest. */
export function announceOrgPolicyChanged(): void {
  listeners.forEach((fn) => {
    try {
      fn();
    } catch {
      // A console cache failing to refresh must not surface as a failed save.
    }
  });
}
